import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import {
  useSEOMeta,
  generateFAQPageSchema,
  generateBreadcrumbSchema,
} from "@/components/SEO";

export default function AboutPage() {
  const location = useLocation();

  useSEOMeta({
    title: "About Us - BHODEV Cameroon | Mission, Vision & Story",
    description:
      "Learn about Bogi Health Organization for Development (BHODEV) — our story since 2014, mission, vision, values, policies, and leadership. An NGO serving vulnerable communities across Cameroon.",
    keywords:
      "BHODEV about us, Cameroon NGO mission, BHODEV story, community health Cameroon, NGO values, humanitarian Cameroon, Hans Asa Tanga, BHODEV founder, sustainable development Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/about",
    ogImage:
      "https://readdy.ai/api/search-image?query=BHODEV%20NGO%20team%20meeting%20in%20Cameroon%20community%20health%20workers%20outdoor%20gathering%20professional%20humanitarian%20organization%20warm%20natural%20light%20documentary&width=1200&height=630&seq=about-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "About Us", url: "https://bhodevcameroon.org/about" },
      ]),
      ...generateFAQPageSchema([
        {
          question: "What is BHODEV?",
          answer:
            "Bogi Health Organization for Development (BHODEV) is an NGO working to promote health, peace, social justice and sustainable development in Cameroon, founded in 2014 and legalized in 2019.",
        },
        {
          question: "Where does BHODEV operate?",
          answer:
            "BHODEV primarily operates in Cameroon, with a focus on the North West and South West Regions. Our headquarters is in Buea, South West Region.",
        },
        {
          question: "What are BHODEV's core values?",
          answer:
            "Our core values are Transparency, Commitment, Sustainability, and Accountability — guiding every decision we make and every community we serve.",
        },
        {
          question: "Who founded BHODEV?",
          answer:
            "BHODEV was founded by Mr Hans Asa Tanga, who has been on the forefront of humanitarian and developmental response in Cameroon since 2014.",
        },
      ]),
    },
  });

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <main className="bg-white font-inter">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/company-assets%2FIMG_6332%20-%20BOGI%20HEALTH%20ORGANIZATION%20FOR%20DEVELOPMENT%20-%20BHODEV.jpeg?alt=media&token=e798946f-695a-4d45-b6cc-96cfd3864a66"
          alt="BHODEV team in Cameroon"
          title="BHODEV team in Cameroon"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        <div className="relative z-10 text-center text-white px-4 w-full max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
            <i className="ri-heart-pulse-line text-primary"></i>
            Bogi Health Organization for Development
          </span>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 leading-tight tracking-tight">
            About <span className="text-primary">BHODEV</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Independent. Inclusive. Impact-driven. Serving the most vulnerable communities in Cameroon since 2014.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href="#our-story" className="bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary-dark transition-all duration-300 whitespace-nowrap cursor-pointer">
              Our Story
            </a>
            <a href="#mission" className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Mission &amp; Vision
            </a>
          </div>
        </div>
      </section>

      {/* ── QUICK STATS ── */}
      <section className="bg-stone-900 py-10 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "2014", label: "Founded", icon: "ri-calendar-line" },
            { value: "50K+", label: "People Impacted", icon: "ri-group-line" },
            { value: "4", label: "Core Sectors", icon: "ri-focus-3-line" },
            { value: "8+", label: "SDGs Addressed", icon: "ri-global-line" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-1">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/20 mb-1">
                <i className={`${stat.icon} text-primary text-lg`}></i>
              </div>
              <p className="text-3xl font-bold text-white">{stat.value}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── OUR STORY ── */}
      <section id="our-story" className="py-24 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: image */}
            <div className="relative">
              <div className="w-full h-[420px] rounded-2xl overflow-hidden">
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fdistribution-of-water-hygiene-and-sanitation-kits-to-victims-of-flood-incident-in-limbe%2FIMG-20231011-WA0007.jpg?alt=media&token=4b9c57c0-1a32-41ae-ae71-80efc05a4e74"
                  alt="BHODEV distributing water, hygiene and sanitation kits to flood victims in Limbe"
                  title="BHODEV distributing water, hygiene and sanitation kits to flood victims in Limbe"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-white rounded-2xl px-6 py-4 shadow-xl">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-sm font-medium text-white/80">Years of Service</p>
              </div>
              <div className="absolute -top-4 -left-4 bg-white rounded-xl px-4 py-3 shadow-lg border border-stone-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary rounded-full">
                    <i className="ri-map-pin-line text-primary text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-800">Buea, Cameroon</p>
                    <p className="text-xs text-stone-500">North West &amp; South West</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: text */}
            <div>
              <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">Our Story</span>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6 leading-tight">
                How It All <span className="text-primary">Began</span>
              </h2>
              <div className="space-y-5 text-stone-600 text-base leading-relaxed">
                <p>
                  <strong className="text-stone-800">Bogi Health Organization for Development (BHODEV)</strong> is an NGO working to promote health, peace, social justice &amp; sustainable development in Cameroon, founded in <strong>2014</strong> and legalized in <strong>2019</strong>. BHODEV was founded by a group of medical doctors, nurses, entrepreneurs and psychologists who were concerned with the predicament of the rural communities especially the interior and inaccessible areas making the inhabitants vulnerable such as persons with disabilities, displaced women and children, widows, orphans, school dropouts, minority groups who are all beneficiary of our programs.
                </p>
                <p>
                  BHODEV was born in order to assist the vulnerable population in the rural communities of the North West and South West Region of Cameroon. This group of medical doctors, nurses, entrepreneurs, psychologist and other well-wishers pioneer by the president &amp; CEO, Mr Hans Asa Tanga interest was not only in serving those rendered vulnerable but advocating for them and denounce the injustice of the vulnerable people living in the rural communities.
                </p>
                <p>
                  Accessing the rural communities we discovered that they were faced with health challenges, inequality, gender-based violence, poor hygiene and sanitation, inadequate educational facilities which brought fort the department of Education and Gender. In the course of carrying out the activities in the education and gender department we realized that there was a need for the <strong>livelihood and environmental sustainability</strong> department as not everyone could be reached through educational sector.
                </p>
                <p>
                  We are independent of government, religions and political parties. We carry out developmental activities that meet the needs of the present and future generation without compromising the ability of communities to take control of their socio-economic development that generate employment and eliminate extreme poverty.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 mt-8">
                {["Health", "Education", "Economic Security", "Protection (GBV)"].map((tag) => (
                  <span key={tag} className="bg-secondary text-primary text-xs font-semibold px-3 py-1.5 rounded-full">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section id="mission" className="py-24 px-4 bg-stone-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">Purpose &amp; Direction</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
              Mission &amp; <span className="text-primary">Vision</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Mission */}
            <div className="relative bg-white rounded-2xl p-10 border border-stone-100 overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute top-0 left-0 w-1.5 h-full bg-primary rounded-l-2xl"></div>
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary/5 rounded-full"></div>
              <div className="w-14 h-14 flex items-center justify-center bg-primary/10 rounded-2xl mb-6">
                <i className="ri-focus-3-line text-primary text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4">Our Mission</h3>
              <p className="text-stone-600 text-lg leading-relaxed font-medium">
                Community Health at Heart
              </p>
              <p className="text-stone-500 text-sm mt-3 leading-relaxed">
                To promote health, peace, social justice and sustainable development in Cameroon by empowering vulnerable communities through inclusive programs that address health, education, economic security, and protection.
              </p>
              <div className="mt-6 pt-6 border-t border-stone-100 flex items-center gap-2 text-primary text-sm font-semibold">
                <i className="ri-heart-pulse-line"></i>
                <span>Health &middot; Peace &middot; Justice &middot; Development</span>
              </div>
            </div>

            {/* Vision */}
            <div className="relative bg-primary rounded-2xl p-10 overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-white/10 rounded-full"></div>
              <div className="absolute top-6 right-6 w-20 h-20 bg-white/5 rounded-full"></div>
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl mb-6">
                <i className="ri-eye-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-white text-lg leading-relaxed font-medium">
                We strive to create an empowered community that provides good health for all.
              </p>
              <p className="text-white/70 text-sm mt-3 leading-relaxed">
                A Cameroon where every individual, regardless of location or circumstance, has access to quality health services, education, and economic opportunity.
              </p>
              <div className="mt-6 pt-6 border-t border-white/20 flex items-center gap-2 text-white/80 text-sm font-semibold">
                <i className="ri-global-line"></i>
                <span>Empowered &middot; Inclusive &middot; Sustainable</span>
              </div>
            </div>
          </div>

          {/* SDG alignment */}
          <div className="bg-white rounded-2xl p-8 border border-stone-100">
            <h3 className="text-lg font-bold text-stone-900 mb-6 text-center">Aligned with UN Sustainable Development Goals</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { num: "SDG 1", label: "No Poverty" },
                { num: "SDG 3", label: "Good Health" },
                { num: "SDG 4", label: "Quality Education" },
                { num: "SDG 5", label: "Gender Equality" },
                { num: "SDG 6", label: "Clean Water" },
                { num: "SDG 10", label: "Reduced Inequalities" },
                { num: "SDG 16", label: "Peace &amp; Justice" },
                { num: "SDG 17", label: "Partnerships" },
              ].map((sdg) => (
                <div key={sdg.num} className="flex items-center gap-2 bg-secondary/60 px-4 py-2 rounded-full">
                  <span className="text-primary font-bold text-xs">{sdg.num}</span>
                  <span className="text-stone-700 text-xs" dangerouslySetInnerHTML={{ __html: sdg.label }}></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES & POLICIES ── */}
      <section id="values" className="py-24 px-4 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">What We Stand For</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
              Values &amp; <span className="text-primary">Policies</span>
            </h2>
            <p className="text-stone-500 mt-4 max-w-xl mx-auto text-base">
              Our core values guide every decision we make and every community we serve.
            </p>
          </div>

          {/* Values grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              {
                letter: "T",
                title: "Transparency",
                subtitle: "Our Work gives us Strength",
                desc: "We operate with full openness in our programs, finances, and partnerships — building trust with every stakeholder.",
                bg: "bg-emerald-400",
                text: "text-white",
              },
              {
                letter: "C",
                title: "Commitment",
                subtitle: "Commitment in serving the Society",
                desc: "We are deeply committed to the communities we serve, showing up consistently and delivering on our promises.",
                bg: "bg-teal-300",
                text: "text-stone-800",
              },
              {
                letter: "S",
                title: "Sustainability",
                subtitle: "What we do is meant to last",
                desc: "Every program we design is built for long-term impact, ensuring communities can thrive independently.",
                bg: "bg-slate-300",
                text: "text-stone-800",
              },
              {
                letter: "A",
                title: "Accountability",
                subtitle: "Accountable to our partners, community and ourselves",
                desc: "We hold ourselves to the highest standards of responsibility toward our beneficiaries, donors, and partners.",
                bg: "bg-slate-500",
                text: "text-white",
              },
            ].map((val) => (
              <div
                key={val.title}
                className={`${val.bg} ${val.text} rounded-2xl p-8 flex flex-col gap-3 hover:-translate-y-1 transition-transform duration-300 cursor-default`}
              >
                <p className="text-5xl font-black opacity-30">{val.letter}</p>
                <div>
                  <h3 className="text-xl font-bold">{val.title}</h3>
                  <p className="text-sm opacity-75 mt-1 font-medium">{val.subtitle}</p>
                </div>
                <p className="text-sm opacity-80 leading-relaxed mt-1">{val.desc}</p>
              </div>
            ))}
          </div>

          {/* Slogan & Motto */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <div className="bg-primary rounded-2xl p-8 text-white text-center">
              <p className="text-xs font-bold uppercase tracking-widest opacity-70 mb-2">Our Slogan</p>
              <p className="text-2xl font-bold">Good Health for All</p>
            </div>
            <div className="bg-secondary rounded-2xl p-8 text-center">
              <p className="text-xs font-bold uppercase tracking-widest text-primary/70 mb-2">Our Motto</p>
              <p className="text-2xl font-bold text-stone-800">Humility, Dedication, Service</p>
            </div>
          </div>

          {/* Policies */}
          <div>
            <h3 className="text-2xl font-bold text-stone-900 mb-8 text-center">Our Policies</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  title: "PSEA",
                  full: "Protection from Sexual Exploitation and Abuse",
                  icon: "ri-shield-check-line",
                  desc: "We maintain a zero-tolerance policy toward sexual exploitation and abuse in all our operations.",
                  color: "bg-slate-600 text-white",
                },
                {
                  title: "Humanitarian Principles and Access",
                  full: "Humanity &middot; Neutrality &middot; Impartiality &middot; Independence",
                  icon: "ri-hand-heart-line",
                  desc: "We uphold the four core humanitarian principles in every program and field operation.",
                  color: "bg-slate-200 text-stone-800",
                },
                {
                  title: "Anti-Corruption and Bribery",
                  full: "Zero Tolerance Policy",
                  icon: "ri-scales-3-line",
                  desc: "We enforce strict anti-corruption measures across all levels of the organization.",
                  color: "bg-emerald-100 text-stone-800",
                },
                {
                  title: "Bsafe",
                  full: "Staff Safety &amp; Security Policy",
                  icon: "ri-shield-user-line",
                  desc: "The safety and security of our staff and volunteers is a top organizational priority.",
                  color: "bg-teal-100 text-stone-800",
                },
              ].map((policy) => (
                <div key={policy.title} className={`${policy.color} rounded-2xl p-7 flex flex-col gap-3 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className={`${policy.icon} text-lg`}></i>
                  </div>
                  <h4 className="font-bold text-base leading-snug">{policy.title}</h4>
                  <p className="text-xs opacity-70 font-medium" dangerouslySetInnerHTML={{ __html: policy.full }}></p>
                  <p className="text-sm opacity-80 leading-relaxed">{policy.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOUNDER ── */}
      <section id="founder" className="py-24 px-4 bg-stone-50 scroll-mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block bg-secondary text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">Leadership</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 leading-tight">
              Meet the <span className="text-primary">Founder</span>
            </h2>
          </div>

          <div className="bg-white rounded-3xl overflow-hidden border border-stone-100" itemScope itemType="https://schema.org/Person">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Photo */}
              <div className="relative h-[420px] lg:h-auto min-h-[420px]">
                <img
                  src="https://storage.readdy-site.link/project_files/4e9a12fb-cddb-4082-86d9-a8d48c5e1af1/6e8d2f98-3476-401d-99b3-c463c9d97a43_Asa---BOGI-HEALTH-ORGANIZATION-FOR-DEVELOPMENT---BHODEV.jpeg?v=9d2b646e05d33535a1bb7157bbe2d070"
                  alt="Mr Hans Asa Tanga - President &amp; CEO of BHODEV"
                  title="Mr Hans Asa Tanga - President &amp; CEO of BHODEV"
                  className="w-full h-full object-cover object-top"
                  itemProp="image"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 lg:hidden">
                  <p className="text-white font-bold text-xl" itemProp="name">Mr Hans Asa Tanga</p>
                  <p className="text-white/70 text-sm" itemProp="jobTitle">President &amp; CEO, BHODEV</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="hidden lg:block mb-6">
                  <h3 className="text-3xl font-bold text-stone-900" itemProp="name">Mr Hans Asa Tanga</h3>
                  <p className="text-primary font-semibold mt-1" itemProp="jobTitle">President &amp; CEO, BHODEV Cameroon</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-7">
                  {["Humanitarian Leader", "Community Advocate", "Health Champion", "Since 2014"].map((tag) => (
                    <span key={tag} className="bg-secondary text-primary text-xs font-semibold px-3 py-1 rounded-full">{tag}</span>
                  ))}
                </div>

                <div className="space-y-4 text-stone-600 text-base leading-relaxed" itemProp="description">
                  <p>
                    <strong className="text-stone-800">Hans Asa Tanga</strong> has been on the forefront of humanitarian and developmental response via BHODEV in the North West and South West Region since 2014. His focus has been to empower the vulnerable and marginalized population as he provides persons around the world with unique opportunities to connect and assist people in need around the African communities and create great impact in the lives of individuals as well as the community as a whole.
                  </p>
                  <p>
                    He has empowered more than <strong className="text-stone-800">850 women and youths</strong> in different capacities through micro project implementation, monitoring and evaluation, business planning and implementation. More than <strong className="text-stone-800">50 campaigns and projects</strong> on the fight against malaria in the hinterland.
                  </p>
                </div>

                {/* Impact stats */}
                <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-stone-100">
                  {[
                    { value: "850+", label: "Women &amp; Youths Empowered" },
                    { value: "50+", label: "Campaigns &amp; Projects" },
                    { value: "10+", label: "Years of Leadership" },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <p className="text-2xl font-bold text-primary">{stat.value}</p>
                      <p className="text-xs text-stone-500 mt-1 leading-tight" dangerouslySetInnerHTML={{ __html: stat.label }}></p>
                    </div>
                  ))}
                </div>

                {/* Social links */}
                <div className="flex items-center gap-3 mt-8">
                  <p className="text-sm text-stone-500 font-medium">Connect:</p>
                  {[
                    { href: "https://www.linkedin.com/in/bogi-health-organization-for-development-bhodev-805471276/", icon: "ri-linkedin-fill", label: "LinkedIn" },
                    { href: "https://web.facebook.com/profile.php?id=100094000555791", icon: "ri-facebook-fill", label: "Facebook" },
                    { href: "https://twitter.com/bhodevCameroon", icon: "ri-twitter-x-fill", label: "Twitter" },
                    { href: "https://www.instagram.com/bhodevcameroon_ngo/", icon: "ri-instagram-line", label: "Instagram" },
                  ].map((s) => (
                    <a
                      key={s.href}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      aria-label={s.label}
                      itemProp="sameAs"
                      className="w-9 h-9 flex items-center justify-center bg-stone-100 rounded-lg hover:bg-primary hover:text-white text-stone-600 transition-all duration-300 cursor-pointer"
                    >
                      <i className={`${s.icon} text-sm`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-4 bg-primary">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Join BHODEV in building healthier, more resilient communities across Cameroon.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="/get-involved" className="bg-white text-primary px-8 py-3.5 rounded-full font-bold text-sm hover:bg-stone-100 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Get Involved
            </a>
            <a href="/contact" className="bg-white/10 border border-white/30 text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}